import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusBadge } from '../components/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Check, X, MessageSquare, Edit, FileText, History, User } from 'lucide-react';
import { toast } from 'sonner';
import { reloadPortalStartupMaps } from '../lib/portalApi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// History actions, as shown in Version History
const ACTION_LABELS = { create: 'Submitted', update: 'Edited', resubmit: 'Resubmitted', approve: 'Approved', reject: 'Rejected' };
const ACTION_STYLES = {
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-amber-100 text-amber-800',
  resubmit: 'bg-amber-100 text-amber-800',
  approve: 'bg-green-100 text-green-800',
  reject: 'bg-red-100 text-red-800',
};

const DetailRow = ({ label, value }) => (
  <div className="py-3 border-b border-gray-100 last:border-0">
    <dt className="text-sm text-muted-foreground mb-1">{label}</dt>
    <dd className="font-medium break-words">{value ?? '—'}</dd>
  </div>
);

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

export default function UnitConversionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('from') === 'pending' ? '/pending-approvals' : '/unit-conversions';
  const isExisting = searchParams.get('source') === 'existing';

  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, canApprove } = useAuth();

  // Version history and comments: one entry per version (existing conversions have none: they were added directly)
  const [history, setHistory] = useState([]);

  const fetchConversion = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/unit-conversions/${id}`, { params: isExisting ? { source: 'existing' } : {} });
      setConversion(response.data);
    } catch (error) {
      toast.error('Failed to load unit conversion');
    } finally {
      setLoading(false);
    }
  }, [id, isExisting]);

  useEffect(() => {
    fetchConversion();
  }, [fetchConversion]);

  useEffect(() => {
    if (isExisting) return;
    axios.get(`${API_URL}/unit-conversions/${id}/history`)
      .then(res => setHistory(res.data.history))
      .catch(() => { /* history tabs just stay empty */ });
  }, [id, isExisting, conversion?.version]);

  const closeDialog = () => {
    setApprovalDialog({ open: false, action: null });
    setComment('');
  };

  const handleApproval = async () => {
    const { action } = approvalDialog;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/unit-conversions/${id}/approve`, { action, comment: comment || null });
      toast.success(`Unit conversion ${action === 'approve' ? 'approved' : 'rejected'}`);
      if (action === 'approve') reloadPortalStartupMaps();
      closeDialog();
      if (backUrl === '/pending-approvals') navigate(backUrl);
      else fetchConversion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!conversion) {
    return (
      <DashboardLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Unit conversion not found</p>
          <Button className="mt-4" onClick={() => navigate('/unit-conversions')}>Back to Unit Conversions</Button>
        </div>
      </DashboardLayout>
    );
  }

  const comments = history.filter(entry => entry.comment);

  // Pending / rejected submissions can be edited by whoever created them, or by an admin
  const canEditConversion =
    ['pending', 'rejected'].includes(conversion.status) &&
    (conversion.user_email === user?.email || user?.role === 'admin');

  const title = isExisting
    ? `#${conversion.id}`
    : (conversion.unit_conversion_id ? `#${conversion.unit_conversion_id}` : '(not approved yet)');

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="unit-conversion-detail-page">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold text-foreground">Unit Conversion {title}</h1>
                <StatusBadge status={conversion.status} />
              </div>
              <p className="text-muted-foreground mt-1">
                1 {conversion.conversion_from} = {conversion.value} {conversion.conversion_to}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {canEditConversion && (
              <Link to={`/unit-conversions/${conversion.id}/edit`}>
                <Button variant="outline" data-testid="edit-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  {conversion.status === 'rejected' ? 'Edit & Resubmit' : 'Edit'}
                </Button>
              </Link>
            )}
            {canApprove() && conversion.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setApprovalDialog({ open: true, action: 'reject' })}
                  data-testid="reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setApprovalDialog({ open: true, action: 'approve' })}
                  data-testid="approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>

        {conversion.status === 'rejected' && conversion.comment && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Rejection Reason</h3>
                <p className="text-red-700">{conversion.comment}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="w-4 h-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
              <History className="w-4 h-4" />
              Version History ({history.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2" data-testid="tab-comments">
              <MessageSquare className="w-4 h-4" />
              Comments ({comments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-gray-200">
                <CardHeader><CardTitle className="font-heading text-lg">Conversion</CardTitle></CardHeader>
                <CardContent>
                  <DetailRow label="Unit Category" value={conversion.unit_category} />
                  <DetailRow label="Conversion From" value={conversion.conversion_from} />
                  <DetailRow label="Conversion To" value={conversion.conversion_to} />
                  <DetailRow label="Value" value={conversion.value} />
                </CardContent>
              </Card>

              <Card className="border-gray-200">
                <CardHeader><CardTitle className="font-heading text-lg">Submission</CardTitle></CardHeader>
                <CardContent>
                  <DetailRow label="Status" value={<StatusBadge status={conversion.status} />} />
                  <DetailRow label="Created By" value={conversion.user_email} />
                  <DetailRow label="Created At" value={formatDate(conversion.created_at)} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Version History</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{isExisting ? 'This conversion was added directly in the database, so it has no Atlas history' : 'No history yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((entry) => (
                      <div key={entry.version} className="p-4 border rounded-lg" data-testid={`history-entry-${entry.version}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-muted-foreground">v{entry.version}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                              {ACTION_LABELS[entry.action] || entry.action}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</span>
                        </div>
                        {['create', 'update', 'resubmit'].includes(entry.action) && (
                          <p className="text-sm mb-2">1 {entry.data.conversion_from} = {entry.data.value} {entry.data.conversion_to}</p>
                        )}
                        {entry.comment && <p className="text-sm mb-2">"{entry.comment}"</p>}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comments">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Comments</CardTitle></CardHeader>
              <CardContent>
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((entry) => (
                      <div
                        key={entry.version}
                        className={`p-4 border rounded-lg ${entry.action === 'reject' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}
                        data-testid={`comment-entry-${entry.version}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[entry.action]}`}>
                            {entry.action === 'reject' ? 'Rejection Feedback' : 'Approval Note'}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</span>
                        </div>
                        <p className="text-sm mb-2">{entry.comment}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{approvalDialog.action === 'approve' ? 'Approve' : 'Reject'} Unit Conversion</DialogTitle>
              <DialogDescription>
                {approvalDialog.action === 'approve'
                  ? 'This will add the conversion to the unit_conversion table in preprod and production.'
                  : 'Please provide a reason for rejecting this conversion.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="comment">
                Comment {approvalDialog.action === 'reject' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={approvalDialog.action === 'reject' ? 'Please provide reason for rejection' : 'Optional comment'}
                rows={3}
                data-testid="approval-comment"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button
                onClick={handleApproval}
                disabled={submitting || (approvalDialog.action === 'reject' && !comment)}
                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-approval-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {approvalDialog.action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
